import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const WAIT_MS = 6000;
const MARK_MS = 4000;

/**
 * Arriving from the search box (`?find=SB-2026-10-0002`): scrolls to the first visible table row that contains the text and marks it for a
 * few seconds. The list may still be loading, so it keeps looking for a few seconds as the page fills in; if the row is not there (a
 * long list, or something filtered out) it says so. The address is tidied afterwards so a refresh does not repeat it. Mounted once, in the layout.
 */
export default function useFindOnPage() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const text = params.get('find');
    if (!text) return undefined;
    const needle = text.toLowerCase();
    let finished = false;
    let observer = null;
    let timer = null;

    const tidy = () => {
      const rest = new URLSearchParams(window.location.search);
      rest.delete('find');
      const search = rest.toString();
      navigate({ pathname: window.location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    };
    const finish = (found) => {
      if (finished) return;
      finished = true;
      if (observer) observer.disconnect();
      clearTimeout(timer);
      if (!found) toast.info(`"${text}" is not on the list shown. Try the filters on this screen.`);
      tidy();
    };
    const look = () => {
      const row = [...document.querySelectorAll('.main-content tbody tr')]
        .find((tr) => tr.offsetParent !== null && tr.innerText.toLowerCase().includes(needle));
      if (!row) return false;
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      row.classList.add('search-hit');
      setTimeout(() => row.classList.remove('search-hit'), MARK_MS);
      return true;
    };

    if (look()) {
      finish(true);
      return undefined;
    }
    const root = document.querySelector('.main-content') || document.body;
    observer = new MutationObserver(() => { if (look()) finish(true); });
    observer.observe(root, { childList: true, subtree: true });
    timer = setTimeout(() => finish(false), WAIT_MS);
    return () => {
      finished = true;
      if (observer) observer.disconnect();
      clearTimeout(timer);
    };
  }, [location.pathname, location.search, navigate]);
}
